/* Copyright (c) 2020-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Inject, Injectable, Optional } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Observable, BehaviorSubject, of, combineLatest, Subject, forkJoin } from 'rxjs';
import { map, take, mergeMap, tap, mapTo, catchError, concatMap, takeUntil } from 'rxjs/operators';
import { isEmpty, set } from 'lodash';
import { Commands } from 'bridge-commands/commands';
import { Types } from 'bridge-commands/types';
import { BridgeService } from 'crimson/lib/bridge/bridge.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { PlatformDetectorService } from 'crimson/lib/platform-detector/platform-detector.service';
import { SystemInfoService } from 'crimson/lib/system-info-service/system-info.service';
import { DeviceOSType } from 'crimson/lib/platform-detector/platform-detector';
import { SharedStorageService } from 'shared-storage/lib/shared-storage.service';
import { TelemetryService } from 'js-events/lib/telemetry/telemetry.service';
import { StateMachine } from 'utils/lib/state-machine/state-machine';
import { StateMachineError, WildcardState } from 'utils/lib/state-machine/state-machine.models';
import { StateMachineTags } from 'utils/lib/constants/state-machine-constants';
import {
    MicrophoneSettingChangeDef,
    MicrophoneSettingChange,
    MicrophoneInitialized,
    MicrophoneInitializedDef
} from '../../../../../projects/gfn/shared/src/lib/telemetry/TelemetryEvents';
import { MicrophoneState, DefaultMicSettingChangeEventInfo, Undefined, MicWarningToastMessage } from '../constants';
import { NV_PROJECTINTERFACE_CONFIG, ProjectInterfaceConfig } from '../project-interface.config';
import { Actions, StateNames, StateToActionMap } from './constants';

interface GfnMicStateBlock {
    preference: MicrophoneState;
}

interface MicStateData {
    uiMicState: string;
    internalMicState: string;
    blockData: GfnMicStateBlock;
    shouldDefaultEnableMic: boolean;
    osType: DeviceOSType;
    shouldApplyPreference: boolean;
    streamSessionId: string;
    streamSubSessionId: string;
}

@Injectable({
    providedIn: 'root'
})
export class MicrophoneService {
    private readonly blockName = 'gfnMicState';

    private logger: Logger;
    private isInitialized = false;
    private currentMicState: BehaviorSubject<MicrophoneState> = new BehaviorSubject(MicrophoneState.PermissionPending);
    private micInfo = DefaultMicSettingChangeEventInfo;
    private isMicToggled = false;
    private shouldApplyPreference: boolean;
    private stopSubject: Subject<void> = new Subject();

    private micStateMachine: StateMachine<MicStateData> = new StateMachine({
        tag: StateMachineTags.MicrophoneStateMachine,
        states: {
            [StateNames.Start]: {
                onExit: stateData => of(stateData).pipe(mergeMap(data => this.applyUserPreference(data)))
            },
            [StateNames.Initializing]: {
                onEnter: stateData =>
                    of(stateData).pipe(
                        mergeMap(data => this.completeInitialization(data)),
                        mergeMap(data => this.updateStateValue(data))
                    ),
                onExit: stateData => of(stateData).pipe(mergeMap(data => this.sendInitTelemetry(data)))
            },
            [StateNames.Unmuted]: {
                onEnter: stateData =>
                    of(stateData).pipe(
                        mergeMap(data => this.showToastMessage(data)),
                        mergeMap(data => this.writeUserPreference(data)),
                        mergeMap(data => this.sendToggleTelemetry(data)),
                        mergeMap(data => this.updateStateValue(data))
                    )
            },
            [StateNames.Muted]: {
                onEnter: stateData =>
                    of(stateData).pipe(
                        mergeMap(data => this.writeUserPreference(data)),
                        mergeMap(data => this.sendToggleTelemetry(data)),
                        mergeMap(data => this.updateStateValue(data))
                    )
            },
            [StateNames.PermissionPending]: {
                onEnter: stateData =>
                    of(stateData).pipe(
                        mergeMap(data => this.sendToggleTelemetry(data)),
                        mergeMap(data => this.updateStateValue(data))
                    )
            },
            [StateNames.PermissionDenied]: {
                onEnter: stateData =>
                    of(stateData).pipe(
                        mergeMap(data => this.showToastMessage(data)),
                        mergeMap(data => this.sendToggleTelemetry(data)),
                        mergeMap(data => this.updateStateValue(data))
                    )
            },
            [StateNames.NoMicFound]: {
                onEnter: stateData =>
                    of(stateData).pipe(
                        mergeMap(data => this.sendToggleTelemetry(data)),
                        mergeMap(data => this.updateStateValue(data))
                    )
            },
            [StateNames.Stop]: {}
        },
        transitions: {
            [Actions.Initialize]: [
                {
                    from: StateNames.Start,
                    to: StateNames.Initializing
                }
            ],
            [Actions.UnmuteMic]: [
                {
                    from: StateNames.Initializing,
                    to: StateNames.Unmuted
                },
                {
                    from: StateNames.Muted,
                    to: StateNames.Unmuted
                },
                {
                    from: StateNames.PermissionPending,
                    to: StateNames.Unmuted
                },
                {
                    from: StateNames.NoMicFound,
                    to: StateNames.Unmuted
                },
                {
                    from: StateNames.PermissionDenied,
                    to: StateNames.Unmuted
                },
                {
                    from: StateNames.Unmuted,
                    to: StateNames.Unmuted
                }
            ],
            [Actions.MuteMic]: [
                {
                    from: WildcardState,
                    to: StateNames.Muted
                }
            ],
            [Actions.AwaitPermssion]: [
                {
                    from: StateNames.Initializing,
                    to: StateNames.PermissionPending
                },
                {
                    from: StateNames.NoMicFound,
                    to: StateNames.PermissionPending
                }
            ],
            [Actions.DenyPermission]: [
                {
                    from: StateNames.Initializing,
                    to: StateNames.PermissionDenied
                },
                {
                    from: StateNames.PermissionPending,
                    to: StateNames.PermissionDenied
                },
                {
                    from: StateNames.PermissionDenied,
                    to: StateNames.PermissionDenied
                },
                {
                    from: StateNames.Muted,
                    to: StateNames.PermissionDenied
                }
            ],
            [Actions.LookForMic]: [
                {
                    from: WildcardState,
                    to: StateNames.NoMicFound
                }
            ],
            [Actions.StopMic]: [
                {
                    from: WildcardState,
                    to: StateNames.Stop
                }
            ]
        },
        startState: StateNames.Start,
        endState: StateNames.Stop
    });

    constructor(
        loggingService: LoggingService,
        @Optional() @Inject(NV_PROJECTINTERFACE_CONFIG) config: ProjectInterfaceConfig,
        private snackbar: MatSnackBar,
        private bridgeService: BridgeService,
        private translateService: TranslateService,
        private sharedStorageService: SharedStorageService,
        private telemetryService: TelemetryService,
        private systemInfoService: SystemInfoService
    ) {
        this.logger = loggingService.getLogger('MicrophoneService');
        this.shouldApplyPreference = config?.saveMicPreference ?? true;
    }

    public toggleMicrophone(): void {
        this.isMicToggled = true;
        this.currentMicState$.pipe(take(1)).subscribe(currentState => {
            set(this.micInfo, 'startState', currentState);
            if (currentState === MicrophoneState.Unmuted) {
                this.setMicrophoneState(false);
                set(this.micInfo, 'requestedState', MicrophoneState.Muted);
                this.logger.info(`Mic button pressed, current state: ${currentState}, requested state: Muted`);
            } else {
                this.setMicrophoneState(true);
                set(this.micInfo, 'requestedState', MicrophoneState.Unmuted);
                this.logger.info(`Mic button pressed, current state: ${currentState}, requested state: Unmuted`);
            }
        });
    }

    public get currentMicState$(): Observable<MicrophoneState> {
        return this.currentMicState.asObservable();
    }

    // This function triggers an event in ragnarok which provides the current state.
    // Use this triggered event for first time initialization.
    private getMicEvent(): void {
        this.logger.info('Get microphone event called');
        this.bridgeService.executeCommand(Commands.Gfn.Streaming.GetMicState).subscribe();
    }

    private setMicrophoneState(enable: boolean): void {
        this.bridgeService.executeCommand(Commands.Gfn.Streaming.SetMicState, { enable }).subscribe();
    }

    public start(streamSessionId: string, streamSubSessionId: string, bypassPlatformCheck = false): void {
        this.logger.info('Started');

        set(this.micInfo, 'streamSessionId', streamSessionId || Undefined);
        set(this.micInfo, 'subSessionId', streamSubSessionId || Undefined);

        this.setupCallback(streamSessionId, streamSubSessionId, this.shouldApplyPreference, bypassPlatformCheck);
        this.getMicEvent();
    }

    public stop(): void {
        this.logger.info('Stopped');
        this.isInitialized = false;
        this.stopSubject.next();
        this.micStateMachine.on(Actions.StopMic, null).subscribe(
            _ => this.micStateMachine.reset(),
            _ => this.micStateMachine.reset()
        );
    }

    public willBeEnabledOnStreamStart(): Observable<boolean> {
        return forkJoin([
            this.sharedStorageService.readBlock<GfnMicStateBlock>(this.blockName).pipe(catchError(_ => of(null))),
            this.bridgeService.executeCommand(Commands.Gfn.Streaming.ShouldDefaultEnableMic)
        ]).pipe(
            map(
                ([blockData, shouldEnable]) =>
                    shouldEnable && (isEmpty(blockData) || blockData?.preference === MicrophoneState.Unmuted)
            ),
            take(1)
        );
    }

    private setupCallback(
        sessionId: string,
        subSessionId: string,
        shouldApplyPreference: boolean,
        bypassPlatformCheck = false
    ): void {
        this.logger.info('Microphone callback is set up');
        combineLatest([
            this.bridgeService.executeCommand(Commands.Gfn.Streaming.RegisterMicCallback),
            this.sharedStorageService.readBlock<GfnMicStateBlock>(this.blockName).pipe(catchError(_ => of(null))),
            bypassPlatformCheck
                ? of(true)
                : this.bridgeService.executeCommand(Commands.Gfn.Streaming.ShouldDefaultEnableMic),
            this.systemInfoService.getDeviceOSType(),
            of(sessionId),
            of(subSessionId)
        ])
            .pipe(
                map(
                    ([state, blockData, shouldDefaultEnableMic, osType, streamSessionId, streamSubSessionId]: [
                        Types.Gfn.Streaming.RegisterMicCallbackReturn,
                        GfnMicStateBlock,
                        boolean,
                        DeviceOSType,
                        string,
                        string
                    ]) =>
                        this.getActionAndStateData({
                            ...state,
                            blockData,
                            shouldDefaultEnableMic,
                            osType,
                            streamSessionId,
                            streamSubSessionId,
                            shouldApplyPreference
                        })
                ),
                concatMap(([action, stateData]) =>
                    this.micStateMachine.on(action, stateData).pipe(
                        catchError((err: StateMachineError) => {
                            this.logger.info('error occurred:', err.code, err.message, err.data);
                            return of(err);
                        })
                    )
                ),
                takeUntil(this.stopSubject)
            )
            .subscribe({
                next: _ => this.logger.info('Current mic state:', this.micStateMachine.currentState),
                error: err => this.logger.info('Unexpected error occurred:', err),
                complete: () => this.logger.info('Observable stream terminated')
            });
    }

    private getActionAndStateData(state: MicStateData): [Actions, MicStateData] {
        this.logger.info('getActionAndStateData', state);
        return [
            this.isInitialized ? StateToActionMap.get(state.uiMicState as MicrophoneState) : Actions.Initialize,
            state
        ];
    }

    private updateStateValue(state: MicStateData): Observable<MicStateData> {
        this.logger.info('updateStateValue', state);
        this.currentMicState.next(state.uiMicState as MicrophoneState);
        return of(state);
    }

    private applyUserPreference(state: MicStateData): Observable<MicStateData> {
        this.logger.info('applyUserPreference', state);
        let eventTriggered = false;
        if (state.shouldApplyPreference) {
            if (isEmpty(state.blockData) && state.shouldDefaultEnableMic) {
                this.logger.info('No preference set, unmuting microphone');
                this.setMicrophoneState(true);
                eventTriggered = true;
            } else if (!isEmpty(state.blockData)) {
                this.logger.info('Not initialized, applying stored user preference:', state.blockData.preference);
                if (state.blockData.preference === MicrophoneState.Unmuted) {
                    this.setMicrophoneState(true);
                    eventTriggered = true;
                } else if (state.blockData.preference === MicrophoneState.Muted) {
                    this.setMicrophoneState(false);
                    eventTriggered = true;
                }
            }
        }
        if (!eventTriggered) {
            this.getMicEvent();
        }
        return of(state);
    }

    private sendInitTelemetry(state: MicStateData): Observable<MicStateData> {
        this.logger.info('sendInitTelemetry', state);
        this.telemetryService.push<MicrophoneInitialized>(
            new MicrophoneInitializedDef({
                uiMicState: state.uiMicState,
                internalMicState: state.internalMicState,
                streamSessionId: state.streamSessionId || Undefined,
                streamSubSessionId: state.streamSubSessionId || Undefined
            })
        );
        return of(state);
    }

    private completeInitialization(state: MicStateData): Observable<MicStateData> {
        this.logger.info('completeInitialization', state);
        this.isInitialized = true;
        return of(state);
    }

    private sendToggleTelemetry(state: MicStateData): Observable<MicStateData> {
        this.logger.info('sendToggleTelemetry', state);
        if (this.isMicToggled) {
            set(this.micInfo, 'resultState', state.uiMicState);
            this.logger.info('Sending telemetry:', this.micInfo);
            this.isMicToggled = false;
            this.telemetryService.push<MicrophoneSettingChange>(new MicrophoneSettingChangeDef(this.micInfo));
        }
        return of(state);
    }

    private writeUserPreference(state: MicStateData): Observable<MicStateData> {
        this.logger.info('writeUserPreference', state);
        return this.isMicToggled && state.shouldApplyPreference
            ? this.sharedStorageService.writeBlock(this.blockName, { preference: state.uiMicState }).pipe(mapTo(state))
            : of(state);
    }

    private showToastMessage(state: MicStateData): Observable<MicStateData> {
        this.logger.info('showToastMessage', state);
        let toastMessage = null;
        if (
            state.uiMicState === MicrophoneState.Unmuted &&
            state.osType === DeviceOSType.ChromeOS &&
            !state.shouldDefaultEnableMic
        ) {
            toastMessage = MicWarningToastMessage.PerformaceWarning;
        } else if (state.uiMicState === MicrophoneState.PermissionDenied) {
            if (PlatformDetectorService.isBrowserPlatform()) {
                toastMessage = MicWarningToastMessage.PermissionBlocked;
            } else if (PlatformDetectorService.isCefPlatform() && this.isMicToggled) {
                if (state.osType === DeviceOSType.MacOS) {
                    toastMessage = MicWarningToastMessage.PermissionBlockedMacOS;
                } else if (state.osType === DeviceOSType.Windows) {
                    toastMessage = MicWarningToastMessage.PermissionBlockedWindows;
                }
            }
        }

        return toastMessage ? this.showToastMessageInternal(toastMessage).pipe(mapTo(state)) : of(state);
    }

    private showToastMessageInternal(message: string): Observable<string> {
        this.logger.info('Showing toast notification');
        return this.translateService.get(message).pipe(
            tap(res => {
                const config: MatSnackBarConfig = {
                    duration: 7000,
                    horizontalPosition: 'left',
                    verticalPosition: 'bottom',
                    panelClass: 'nv-hig'
                };
                this.snackbar.open(res, '', config);
            })
        );
    }
}
